import {
  Flex,
  Text,
  Icon,
  Box,
  Image,
  Center,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { MdOutlineCloudUpload, MdDescription } from "react-icons/md";
import { useEffect, useState } from "react";

interface DropzoneProps {
  label: string;
  onFileSelect?: (file: File | null) => void;
  existingFile?: string | { fileUrl: string };
}

function Dropzone({ label, onFileSelect, existingFile }: DropzoneProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<"pdf" | "image" | "other" | null>(null);

  // Alert state
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [centerAlertStatus, setCenterAlertStatus] =
    useState<"success" | "error">("error");

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: [
      ".pdf",
      ".jpg",
      ".jpeg",
      ".png",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".csv",
    ],
    maxFiles: 1,

    onDrop: (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      const file = acceptedFiles[0];
      const previewUrl = URL.createObjectURL(file);

      setUploadedFile(file);
      setFilePreview(previewUrl);

      if (file.type === "application/pdf") {
        setPreviewType("pdf");
      } else if (file.type.startsWith("image/")) {
        setPreviewType("image");
      } else {
        setPreviewType("other");
      }
      onFileSelect?.(file);
    },


    onDropRejected: () => {
      setCenterAlertMessage(
        "Please upload PDF, JPG, PNG, DOC, DOCX, XLS, or CSV."
      );
      setCenterAlertStatus("error");
      setCenterAlertVisible(true);
      setTimeout(() => setCenterAlertVisible(false), 3000);
    },
  });

  // Handle existing file preview
  useEffect(() => {
    if (!existingFile) {
      setUploadedFile(null);
      setFilePreview(null);
      setPreviewType(null);
      return;
    }

    let url = "";

    if (typeof existingFile === "object" && existingFile?.fileUrl) {
      url = existingFile.fileUrl;
    } else if (typeof existingFile === "string") {
      url = existingFile;
    }

    if (!url) return;
    const cleanUrl = url.split("?")[0];
    const fileName = cleanUrl.split("/").pop() || "";
    const ext = fileName.split(".").pop()?.toLowerCase();

    setUploadedFile(null);
    setFilePreview(url);

    if (ext === "pdf") {
      setPreviewType("pdf");
    } else if (["jpg", "jpeg", "png", "webp"].includes(ext ?? "")) {
      setPreviewType("image");
    } else {
      setPreviewType("other");
    }

  }, [existingFile]);

  return (
    <>
      {/* Centered Alert */}
      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            bg={centerAlertStatus === "success" ? "green.50" : "red.50"}
            borderRadius="14px"
            boxShadow="lg"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon
              boxSize="40px"
              mr={0}
              color={
                centerAlertStatus === "success"
                  ? "green.600"
                  : "red.600"
              }
            />
            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}

      {/* Dropzone */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        border="2px dashed"
        borderColor={isDragActive ? "blue.400" : "gray.300"}
        borderRadius="16px"
        p="20px"
        w="280px"
        cursor="pointer"
        bg={isDragActive ? "gray.50" : "white"}
        transition="all 0.2s ease"
        {...getRootProps()}
      >
        <input {...getInputProps()} />

        {!filePreview ? (
          <>
            <Icon
              as={MdOutlineCloudUpload}
              w="50px"
              h="50px"
              color="gray.500"
            />
            <Box textAlign="center">
              <Text fontWeight="700" fontSize="lg">
                {label}
              </Text>
              <Text fontSize="sm" color="gray.500">
                PDF, Images, Word, Excel, CSV allowed
              </Text>
            </Box>
          </>
        ) : (
          <>
            {previewType === "pdf" ? (
              <Box
                as="iframe"
                src={filePreview || ""}
                width="100%"
                height="100px"
                borderRadius="8px"
                border="1px solid #ccc"
                mb={2}
                title="PDF Preview"
              />
            ) : previewType === "image" ? (
              <Image
                src={filePreview || ""}
                alt="Uploaded Preview"
                width="100%"
                height="150px"
                // objectFit="cover"
                borderRadius="8px"
                border="1px solid #ccc"
                mb={2}
              />
            ) : (
              <Flex
                direction="column"
                align="center"
                justify="center"
                border="1px solid #ccc"
                borderRadius="8px"
                p="20px"
                mb={2}
                w="100%"
                h="150px"
              >
                <Icon
                  as={MdDescription}
                  w="40px"
                  h="40px"
                  color="blue.500"
                />
                <Text fontSize="sm" mt={2}>
                  File Uploaded
                </Text>
              </Flex>
            )}

            <Text
              mt={1}
              fontSize="sm"
              fontWeight="600"
              textAlign="center"
              noOfLines={1}
            >
              {filePreview?.split("/").pop()?.split("?")[0]}
            </Text>
          </>
        )}
      </Flex>
    </>
  );
}

export default Dropzone;
